import { Button } from '@qr/ui';
import { CircleAlert, LoaderCircle, MailQuestion, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, messageFromError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/**
 * Aceptación de una invitación al equipo.
 *
 * El enlace lleva el identificador y un token que solo existe ahí: en la base
 * de datos se guarda su hash, igual que con una contraseña. Además hay que
 * haber iniciado sesión con la dirección a la que se envió, de modo que
 * reenviar el enlace a otra persona no le da acceso.
 *
 * Es la primera pantalla del producto para quien llega invitado, así que va a
 * juego con la de acceso: mismo armazón, mismos discos de color, mismo tono.
 */

/** El armazón que comparten los cuatro estados de esta pantalla. */
function Marco({ children }: { readonly children: React.ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-ground px-5 py-10">
      <div className="flex w-full max-w-[24rem] flex-col gap-5">{children}</div>
    </main>
  );
}

function Cabecera({
  icono,
  tono,
  titulo,
  children,
}: {
  readonly icono: React.ReactNode;
  readonly tono: 'marca' | 'malo';
  readonly titulo: string;
  readonly children: React.ReactNode;
}) {
  return (
    <header className="flex flex-col gap-3">
      <span
        aria-hidden
        className={`grid size-12 place-items-center rounded-xl ${
          tono === 'malo' ? 'bg-bad-soft text-bad' : 'bg-brand-soft text-brand-deep'
        }`}
      >
        {icono}
      </span>
      <h1 className="m-0 text-2xl font-bold tracking-tight">{titulo}</h1>
      <p className="m-0 text-[14.5px] leading-relaxed text-ink-2">{children}</p>
    </header>
  );
}

export function AcceptInvite() {
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const inviteId = params.get('id');
  const token = params.get('token');

  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (loading || !user || !inviteId || !token || attempted.current) return;
    attempted.current = true;
    setWorking(true);

    api
      .acceptInvite({ inviteId, token })
      .then(() => navigate('/', { replace: true }))
      .catch((caught: unknown) => setError(messageFromError(caught)))
      .finally(() => setWorking(false));
  }, [loading, user, inviteId, token, navigate]);

  if (!inviteId || !token) {
    return (
      <Marco>
        <Cabecera
          icono={<MailQuestion size={23} strokeWidth={1.8} />}
          tono="malo"
          titulo="A este enlace le faltan datos"
        >
          Puede que se cortara al copiarlo o al pasarlo por un chat. Pídele a quien
          te invitó que te lo mande otra vez.
        </Cabecera>
      </Marco>
    );
  }

  if (loading) return null;

  // Sin sesión no se puede aceptar: primero hay que entrar con el correo al
  // que iba dirigida la invitación.
  if (!user) {
    return (
      <Marco>
        <Cabecera
          icono={<UserPlus size={23} strokeWidth={1.8} />}
          tono="marca"
          titulo="Te han invitado a un equipo"
        >
          Entra con el correo al que te llegó la invitación y te añadimos solo.
          Tiene que ser esa dirección: es lo que impide que el enlace sirva en
          otras manos.
        </Cabecera>

        <div>
          <Link to={`/acceso?redirect=${encodeURIComponent(globalThis.location.href)}`}>
            <Button>Entrar y aceptar</Button>
          </Link>
        </div>
      </Marco>
    );
  }

  return (
    <Marco>
      {working ? (
        <div className="flex flex-col items-center gap-3 text-center">
          <LoaderCircle size={26} className="animate-spin text-brand" aria-hidden />
          <p className="m-0 text-[14.5px] text-ink-2" role="status">
            Añadiéndote al equipo...
          </p>
        </div>
      ) : null}

      {error ? (
        <>
          <Cabecera
            icono={<CircleAlert size={23} strokeWidth={1.8} />}
            tono="malo"
            titulo="No hemos podido aceptarla"
          >
            {/* Las dos causas de siempre, y las dos con arreglo. */}
            Comprueba que has entrado con el mismo correo al que se envió, y que no
            han pasado más de siete días desde que te la mandaron.
          </Cabecera>

          <p className="m-0 rounded-DEFAULT border border-line bg-surface px-3.5 py-2.5 font-mono text-[12px] break-words text-ink-3">
            {error}
          </p>

          <div>
            <Link to="/">
              <Button variant="secondary">Ir a mi cuenta</Button>
            </Link>
          </div>
        </>
      ) : null}
    </Marco>
  );
}
